const API_BASE = (window as any).__ENV__?.API_BASE || 'http://localhost:3000'

type AudioChunkCallback = (chunk: ArrayBuffer) => void
type TextMessageCallback = (msg: any) => void
type ErrorCallback = (error: string) => void

class TtsStreamClient {
  private ws: WebSocket | null = null
  private audioChunkCb: AudioChunkCallback | null = null
  private textMessageCb: TextMessageCallback | null = null
  private errorCb: ErrorCallback | null = null
  private reconnectAttempts = 0
  private maxReconnects = 3
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private currentVoiceId: string = ''
  private currentToken: string = ''

  async getWsToken(firebaseToken: string): Promise<string> {
    const res = await fetch(`${API_BASE}/v1/tts/ws-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${firebaseToken}`,
      },
    })
    if (!res.ok) {
      throw new Error(`Failed to get WebSocket token: ${res.status}`)
    }
    const data = await res.json()
    return data.token
  }

  connect(voiceId: string, token: string, language?: string, pitch?: string): Promise<void> {
    this.currentVoiceId = voiceId
    this.currentToken = token

    return new Promise((resolve, reject) => {
      const wsBase = API_BASE.replace(/^http/, 'ws')
      let url = `${wsBase}/v1/tts/${encodeURIComponent(voiceId)}/stream/text?token=${encodeURIComponent(token)}`
      if (language) {
        url += `&language=${encodeURIComponent(language)}`
      }
      if (pitch) {
        url += `&pitch=${encodeURIComponent(pitch)}`
      }

      try {
        this.ws = new WebSocket(url)
        this.ws.binaryType = 'arraybuffer'
      } catch (err) {
        reject(new Error(`Failed to create WebSocket: ${err}`))
        return
      }

      this.ws.onopen = () => {
        this.reconnectAttempts = 0
        resolve()
      }

      this.ws.onmessage = (event: MessageEvent) => {
        if (event.data instanceof ArrayBuffer) {
          if (this.audioChunkCb) {
            this.audioChunkCb(event.data)
          }
        } else if (event.data instanceof Blob) {
          event.data.arrayBuffer().then((buf) => {
            if (this.audioChunkCb) {
              this.audioChunkCb(buf)
            }
          })
        } else if (typeof event.data === 'string') {
          try {
            const msg = JSON.parse(event.data)
            if (msg.error) {
              if (this.errorCb) this.errorCb(msg.error)
            } else {
              if (this.textMessageCb) this.textMessageCb(msg)
            }
          } catch {
            // non-JSON text message, ignore
          }
        }
      }

      this.ws.onerror = () => {
        if (this.errorCb) this.errorCb('WebSocket connection error')
      }

      this.ws.onclose = (event: CloseEvent) => {
        if (event.code === 4401) {
          if (this.errorCb) this.errorCb('auth_failed')
          return
        }
        if (event.code === 4402) {
          if (this.errorCb) this.errorCb('insufficient_credits')
          return
        }

        // Reject if we haven't resolved yet (connection never opened)
        if (this.reconnectAttempts === 0 && event.code !== 1000 && event.code !== 1001) {
          // Attempt reconnect with exponential backoff
          if (this.reconnectAttempts < this.maxReconnects) {
            const delay = Math.pow(2, this.reconnectAttempts) * 1000
            this.reconnectAttempts++
            this.reconnectTimeout = setTimeout(() => {
              this.connect(this.currentVoiceId, this.currentToken).catch(() => {
                reject(new Error('Reconnect failed'))
              })
            }, delay)
          } else {
            reject(new Error('WebSocket connection failed'))
          }
        }
      }
    })
  }

  sendText(text: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ text }))
    }
  }

  onAudioChunk(cb: AudioChunkCallback): void {
    this.audioChunkCb = cb
  }

  onTextMessage(cb: TextMessageCallback): void {
    this.textMessageCb = cb
  }

  onError(cb: ErrorCallback): void {
    this.errorCb = cb
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.onopen = null
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
    this.reconnectAttempts = 0
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }
}

export default TtsStreamClient
